import { RefObject, useEffect, useRef, useState } from "react";

/** IntersectionObserver 가 컨테이너 바닥에 도달하기 전 미리 다음 페이지를 로드하는 여유 거리. */
const SENTINEL_ROOT_MARGIN = "200px";

interface UseHistorySentinelPaginationParams {
  /** 패널 스크롤 컨테이너 ref — IntersectionObserver 의 root. */
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  /** react-query v4 fetchNextPage — 실패해도 reject 하지 않고 결과 객체로 resolve 하므로 isError 로 판정한다. */
  fetchNextPage: () => Promise<{ isError: boolean }>;
  /**
   * 쿼리 컨텍스트 전환 시 수동 전환 상태를 리셋하기 위한 키 — 호출부가
   * `projectHistoryQueryKey` 파생값을 넘긴다(세그먼트 구성은 그 함수만 정의하므로
   * 여기에 포맷을 다시 적지 않는다).
   */
  resetKey: string;
}

/**
 * 히스토리 타임라인의 sentinel 기반 페이지네이션 제어.
 *
 * 반환된 `sentinelRef` 를 목록 하단 요소에 걸면 하단 근접 시 다음 페이지를 자동 로드한다
 * (design.md D4). `fetchNextPage` 가 실패하면 `nextPageFailed` 로 전환해 observer 자동
 * 재호출을 막고(관찰자 자체는 계속 살아있다), 호출부는 `handleManualLoadMore` 를 붙인
 * 수동 "더 불러오기" 버튼을 노출한다. 수동 로드가 성공하면 다시 자동 로드로 복귀한다.
 */
export const useHistorySentinelPagination = ({
  scrollContainerRef,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  resetKey,
}: UseHistorySentinelPaginationParams) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [nextPageFailed, setNextPageFailed] = useState(false);

  // 프로젝트/필터가 바뀌면 새 쿼리 컨텍스트이므로 이전 실패로 전환됐던 수동 버튼 상태를 리셋한다.
  useEffect(() => {
    setNextPageFailed(false);
  }, [resetKey]);

  useEffect(() => {
    const root = scrollContainerRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        if (nextPageFailed || isFetchingNextPage) return;
        // v4 fetchNextPage 는 reject 하지 않는다 — 결과의 isError 로 실패를 판정해
        // 수동 버튼으로 전환한다(안 하면 observer 재생성 → 자동 재시도 루프).
        fetchNextPage().then((result) => {
          if (result.isError) setNextPageFailed(true);
        });
      },
      { root, rootMargin: SENTINEL_ROOT_MARGIN }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    scrollContainerRef,
    hasNextPage,
    isFetchingNextPage,
    nextPageFailed,
    fetchNextPage,
  ]);

  const handleManualLoadMore = () => {
    fetchNextPage().then((result) => setNextPageFailed(result.isError));
  };

  return { sentinelRef, nextPageFailed, handleManualLoadMore };
};
